/**
 * Noise filtering for `$exception` events on their way to PostHog Error Tracking.
 *
 * Error Tracking is only useful if the issue list is mostly things we can act on. Two classes of event
 * defeat that:
 *
 *  - **Third-party browser/extension injections** — e.g. Firefox iOS injects a `window.__firefox__`
 *    reader-mode shim and its own code throws. Nothing in this repo can fix it.
 *  - **Vendor-library internals we only ever call through a supported API** — currently mapbox-gl 3.x,
 *    whose terrain raycast and label-placement paths throw on their own state. Over one 30-day window
 *    six fingerprints of this produced ~600 occurrences across 27 users and crowded out everything
 *    else, while being entirely un-actionable from our side.
 *
 * ## Why the filter has to live here
 *
 * There were already two suppressions for the mapbox errors and NEITHER could ever fire in production:
 *
 *  1. `AppGlobalErrorhandler` (ee-portal) sits in Angular's `ErrorHandler`. posthog-js captures
 *     exceptions from its own `window.onerror` / `unhandledrejection` listeners — the captured events
 *     carry `mechanism: { type: 'generic', handled: false }` — so they never pass through Angular at all.
 *  2. Both that handler and the CloudWatch RUM `errors.ignore` predicate matched on the literal string
 *     `mapbox-gl` appearing in the stack. Production stacks only ever name content-hashed bundles
 *     (`/chunk-Q3WIFODS.js`), so the package name is never present. It only ever matched in dev.
 *
 * `before_send` is the only hook that sees what posthog-js actually sends, which is why the deny-list
 * lives here and the other two sites now import from it.
 *
 * ## Matching strategy
 *
 * Match on **minification-stable** signals only. Terser mangles variable names but leaves property and
 * method names alone, which is why frames like `Eh.pointRayIntersection` and `continuePlacement` survive
 * verbatim into the production bundle — verified against real captured events. Package paths and
 * `node_modules` do not survive, so they must never be matched on.
 *
 * ## What is deliberately NOT dropped
 *
 * Anything of ours, even when it looks like environmental noise:
 *  - stale-bundle module-load failures (`Failed to fetch dynamically imported module`, MIME-type
 *    complaints) — see `stale-bundle.ts`; these measure deploy health and must stay countable.
 *  - the pdf.js worker failing to load — that is our asset configuration.
 *  - anything from an EasyEviction frame, however noisy it looks.
 *
 * Residual exception: after the preview dialog stops rendering and unbinds listeners, pdf.js can
 * still reject `startCleanup` mid-render (ISS-202) or fire `onWheel` against a detached container
 * (ISS-197). Those two shapes only, matched below. Other pdf.js errors stay countable.
 *
 * Exception: a bounded Amplify session wait (`AuthSessionTimeoutError` / status-0
 * `Auth Session Timeout`). That stall is handled — credentials are kept, the request is not sent
 * bare — and reporting it as `$exception` was the ISS-182 RUM fingerprint. See the pattern below.
 */

/** A stack frame as posthog-js emits it. `mangled_name` is PostHog's server-side spelling of `function`. */
interface ExceptionFrame {
  function?: string;
  mangled_name?: string;
  filename?: string;
}

interface ExceptionListEntry {
  value?: string;
  type?: string;
  stacktrace?: { frames?: ExceptionFrame[] };
}

/**
 * mapbox-gl internal method names seen throwing in production. Two independent code paths:
 *
 *  - terrain raycast on pointer move — `Map.unproject` → `Transform.pointCoordinate` →
 *    `pointCoordinate3D` → `pointLocation3D` → `pointRayIntersection`, which throws
 *    `Cannot read properties of undefined (reading '0')`. Fires from a `mousemove` handler, so a single
 *    hover across the map produces a burst — that is why occurrence counts hugely exceed session counts.
 *  - label placement on render — `Style._updatePlacement` → `PauseablePlacement.continuePlacement` and
 *    `crossTileSymbolIndex.pruneUnusedLayers`, which throw `... (reading 'get')` /
 *    `E.layout is undefined` against a torn-down or partially-loaded style.
 *
 * We never call any of these ourselves — reaching them means mapbox is walking its own internal state,
 * so their presence anywhere in the stack is sufficient to classify the event as vendor noise. The
 * trade-off is accepted knowingly: if our own bad input ever made mapbox throw here we would suppress
 * that too. Revisit if a map-related user complaint has no matching issue in PostHog.
 */
export const MAPBOX_INTERNAL_FRAMES: readonly string[] = [
  'pointRayIntersection',
  'pointCoordinate3D',
  'pointLocation3D',
  'continuePlacement',
  '_updatePlacement',
  'pruneUnusedLayers',
];

/**
 * Exception messages that are noise regardless of where they come from.
 *
 * Kept narrow and anchored on purpose. An earlier attempt used `/reading 'get'\)/`, which matches a huge
 * family of ordinary application `TypeError`s — that pattern would have hidden real bugs, so message
 * matching here never stands in for the frame check above.
 */
const PDFJS_STARTCLEANUP = /startCleanup: Page \d+ is currently rendering/;

export const NOISE_EXCEPTION_PATTERNS: readonly RegExp[] = [
  // Firefox for iOS injects a reader-mode shim into every page; its own code then throws. Appears as
  // both `undefined is not an object (evaluating 'window.__firefox__.reader')` and
  // `Can't find variable: __firefox__`.
  /\b__firefox__\b/,
  // What browsers report for an exception thrown by a cross-origin script without CORS headers. There
  // is no stack and no file — structurally impossible to action, and it hides the real error by design.
  /^Script error\.?$/,
  // posthog-js reporting on its own network timeout. Circular and not an application fault.
  /PostHog request timed out/i,
  // fetch/XHR aborted because the user navigated away mid-request — normal teardown, not a fault.
  /The user aborted a request/,
  // Bounded Amplify session wait. The portal interceptor converts this into a handled status-0
  // HTTP failure and keeps credentials; the raw Error used to leak as an uncaught js_error_event
  // (ISS-182) whenever an HTTP subscriber had no error handler. posthog-js autocaptures via
  // window.onerror, so the interceptor fix is not enough on its own.
  /^Auth session request timed out after \d+ms$/,
  /^Http failure response for .*: 0 Auth Session Timeout$/,
  // pdf.js WorkerTransport.startCleanup rejects when ngx-extended-pdf-viewer closes (or
  // Angular destroys) the document while a page render task is still in flight. The preview
  // dialog stops rendering and unbinds listeners before that close; this is the leftover
  // rejection if close() still races the render (ISS-202).
  PDFJS_STARTCLEANUP,
];

function frameName(frame: ExceptionFrame): string {
  return frame.function || frame.mangled_name || '';
}

/**
 * True when `exceptions` (the `$exception_list` of a captured event) is third-party noise per the rules
 * documented above. Exported for the Angular `ErrorHandler` and the CloudWatch RUM predicate so all
 * three suppression sites share one definition instead of drifting apart again.
 */
export function isNoiseException(exceptions: ExceptionListEntry[] | undefined): boolean {
  if (!exceptions?.length) {
    return false;
  }
  return exceptions.some(exception => {
    const message = exception.value || '';
    const frames = exception.stacktrace?.frames ?? [];
    const stack = frames.map(frame => `${frameName(frame)} ${frame.filename || ''}`).join('\n');
    if (isBenignPdfJsTeardownError({ message, stack })) {
      return true;
    }
    if (NOISE_EXCEPTION_PATTERNS.some(pattern => pattern.test(message))) {
      return true;
    }
    return frames.some(frame => MAPBOX_INTERNAL_FRAMES.includes(frameName(frame)));
  });
}

/**
 * Same rules as {@link isNoiseException}, applied to a free-text `message`/`stack` pair. For call sites
 * that only ever see an `Error` (Angular's `ErrorHandler`, the CloudWatch RUM `errors.ignore` predicate)
 * and have no structured frame list.
 */
export function isNoiseErrorText(message: string, stack = ''): boolean {
  if (NOISE_EXCEPTION_PATTERNS.some(pattern => pattern.test(message))) {
    return true;
  }
  if (isPdfJsOnWheelParentNodeError(message, stack)) {
    return true;
  }
  // Frame names appear in the raw stack text as `at Eh.pointRayIntersection (...)`. Require a word
  // boundary so a substring of a longer app symbol cannot match.
  return MAPBOX_INTERNAL_FRAMES.some(name => new RegExp(`\\b${name}\\b`).test(stack));
}

/**
 * True when `reason` is the leftover pdf.js teardown fault ISS-197 / ISS-202, and nothing else.
 *
 * Used by the preview dialog to `preventDefault` a residual rejection after it has already
 * stopped rendering and unbound listeners. Message-only for startCleanup; onWheel requires the
 * `parentNode` message AND the viewer frame so a normal app TypeError is not swallowed.
 */
export function isBenignPdfJsTeardownError(reason: unknown): boolean {
  const message = teardownMessage(reason);
  const stack = teardownStack(reason);
  return PDFJS_STARTCLEANUP.test(message) || isPdfJsOnWheelParentNodeError(message, stack);
}

/**
 * ISS-197: `Object.onWheel` in the self-hosted viewer bundle dereferences a container that
 * MatDialog has already removed. The frame name and the `/assets/pdfjs/viewer-` path both
 * survive minification (the asset is copied, not content-hashed).
 */
function isPdfJsOnWheelParentNodeError(message: string, stack: string): boolean {
  return /parentNode/.test(message) && /\bonWheel\b/.test(stack) && /\/assets\/pdfjs\/viewer-/.test(stack);
}

function teardownMessage(reason: unknown): string {
  if (typeof reason === 'string') {
    return reason;
  }
  if (reason instanceof Error) {
    return reason.message || '';
  }
  if (reason && typeof reason === 'object' && 'message' in reason) {
    const message = (reason as { message?: unknown }).message;
    return typeof message === 'string' ? message : '';
  }
  return '';
}

function teardownStack(reason: unknown): string {
  if (reason instanceof Error) {
    return reason.stack || '';
  }
  if (reason && typeof reason === 'object' && 'stack' in reason) {
    const stack = (reason as { stack?: unknown }).stack;
    return typeof stack === 'string' ? stack : '';
  }
  return '';
}

/**
 * `before_send` hook: drops `$exception` events classified as third-party noise, and passes everything
 * else through untouched. Returning `null` is how posthog-js is told to discard an event.
 *
 * Registered AFTER `redactAnalyticsUrls` so anything that survives has already had its URLs scrubbed.
 */
export function dropNoiseExceptions<
  T extends { event?: string; properties?: Record<string, unknown> } | null
>(captureResult: T): T | null {
  if (!captureResult || captureResult.event !== '$exception') {
    return captureResult;
  }
  const exceptions = captureResult.properties?.['$exception_list'] as ExceptionListEntry[] | undefined;
  return isNoiseException(exceptions) ? null : captureResult;
}
